(ns admin.components.composite.questionnaire.settings-form
  (:require
   [reagent-form.core :as rf]
   [reagent-form.utils :as rfu]
   [re-frame.core :as re-frame]

   [admin.utils :as au]
   [kyber.utils :as ku]
   [kyber.components.base.button :as button]
   [kyber.components.base.divider :refer [divider]]
   [kyber.components.base.text :as text]
   [kyber.styles.core :refer [gs styles->classes]]

   [admin.constants :as ac]
   [admin.router.utils :as aru]
   [kyber.components.form-fields.select :as select]
   [admin.components.forms.fields.box-radio :as box-radio]
   [admin.components.forms.fields.confidence-threshold :as confidence-threshold]
   [admin.components.forms.fields.confidence-threshold-toggle
    :as confidence-threshold-toggle]
   [admin.components.forms.fields.pass-threshold :as pass-threshold]
   [admin.components.forms.fields.radio :as radio]
   [admin.components.forms.fields.toggle :as toggle]
   [admin.components.forms.activity :refer [content-container-styles]]))

(def classes
  (styles->classes
   {:settings-form-container
    {:box-shadow (gs [:shadows :shadow-12])
     :margin :auto
     :max-width "1000px"
     "& > div.reagent-form-field"
     {:padding-top 0
      :margin-bottom 0}}

    :settings-field-container
    {:display :flex
     :align-items :center
     :justify-content :space-between
     :width "100%"
     :padding [(gs [:spacing :p12])
               (gs [:spacing :p20])]}

    :test-out-threshold-container
    {:display :flex
     :justify-content :space-between
     "& > div.reagent-form-field"
     {:flex-grow 1
      :padding-right 0}}

    :cta-container
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end
     :padding [(gs [:spacing :p0])
               (gs [:spacing :p20])
               (gs [:spacing :p20])]}

    :radio-container
    (merge content-container-styles
           {:border (str "1px solid " (gs [:colors :error-500]))
            :background-color "rgba(255,0,0,0.1)"
            :flex-direction :column
            :padding-bottom (gs [:spacing :p20])
            :align-items :start})

    :radio-option
    {:margin-top (gs [:spacing :p20])
     "& > label"
     {:margin-left (gs [:spacing :p4])}}}))

(defn- render-percent-display-field
  [{:keys [node]}]
  (let [params
        (second node)

        {:keys [field-key total-number]}
        (:rf/custom-field params)]

    (rfu/ensure-field-key-or-throw field-key node)

    (fn [{:keys [form-state]}]
      (let [current-value (rfu/get-field-value @form-state field-key)]
        [:div {:style {:display :flex
                       :align-items :center}}
         (when (not= :no-score current-value)
           [:<>
            [text/caption-30 :p
             {:style {:margin-right (gs [:spacing :p8])
                      :margin-left (gs [:spacing :p20])
                      :white-space :nowrap}}
             "out of"]

            [text/title-20 :p
             {:style {:margin-right (gs [:spacing :p20])}}
             total-number]])

         (if (= :no-score current-value)
           [text/title-20 :p {:style {:margin-left (gs [:spacing :p20])
                                      :white-space :nowrap}}
            "Not Scored"]
           [text/display-20 :p
            (str (ku/calc-percent-floored total-number
                                          current-value)
                 "%")])]))))

(defn render
  [{:keys [activity-uuid
           boost-uuid
           curriculum-uuid
           questionnaire
           questionnaire-api
           questionnaire-type
           questionnaire-is-updating]} nq]
  (let [[confidence-threshold pass-percent pre-test-pass-percent can-test-out? questions
         max-attempts results-display-strategy feedback-display-strategy questionnaire-uuid
         show-answers-and-feedback-when-passed-or-exhausted? settings-confirmed?]
        (mapv #((nq %) questionnaire) [:confidence-threshold
                                       :pass-percent
                                       :pre-test-pass-percent
                                       :can-test-out?
                                       :questions
                                       :max-attempts
                                       :results-display-strategy
                                       :feedback-display-strategy
                                       :uuid
                                       :show-answers-and-feedback-when-passed-or-exhausted?
                                       :settings-confirmed?])
        graded-questions
        (filter #(-> % :question.context/question :question/has-correctness?) questions)

        graded-assessment-questions
        (when (= questionnaire-type :assessments)
          graded-questions)

        graded-pre-test-questions
        (filter #(or (= "both" (-> % :question.context/assessment-type))
                     (= "pre-test" (-> % :question.context/assessment-type))) graded-assessment-questions)

        graded-post-test-questions
        (filter #(or (= "both" (-> % :question.context/assessment-type))
                     (= "post-test" (-> % :question.context/assessment-type))) graded-assessment-questions)

        question-count
        (count graded-questions)

        pre-test-question-count
        (count graded-pre-test-questions)

        post-test-question-count
        (count graded-post-test-questions)

        is-super-user
        (deref (re-frame/subscribe [:authentication/has-access?
                                    :is-super-user]))

        post-delete-nav-to-url
        (aru/admin-path :activity-assessments
                        :curriculum-uuid curriculum-uuid
                        :activity-uuid activity-uuid
                        :question-step "questions")

        post-update-nav-to-url
        (aru/admin-path :activity-assessments
                        :curriculum-uuid curriculum-uuid
                        :activity-uuid activity-uuid
                        :question-step "messaging")

        questionnaire-update-event
        (if (and (= questionnaire-type :floating)
                 (nil? (:questionnaire/uuid questionnaire)))
          (:questionnaire-create-event questionnaire-api)
          (:questionnaire-update-event questionnaire-api))

        questionnaire-delete-event
        (:questionnaire-delete-event questionnaire-api)

        model-name
        (case questionnaire-type
          :assessments "Assessment"
          :evaluation "Evaluation"
          :floating "Questionnaire"
          (do
            (js/console.error "Invalid Questionnaire Type")
            "Questionnaire"))]
    (fn []
      [rf/form {:id :questionnaire-settings
                :is-submitting questionnaire-is-updating}
       [:form
        {:rf/form
         {:on-submit
          #(if (= questionnaire-type :assessments)
             (re-frame/dispatch
              [questionnaire-update-event
               (cond-> (assoc % :activity/uuid activity-uuid
                                :assessment/settings-confirmed? true)
                 (false? ((nq :confidence-threshold-enabled?) %))
                 (dissoc (nq :confidence-threshold))

                 (nil? ((nq :pre-test-pass-percent) %))
                 (dissoc (nq :pre-test-pass-percent)))
               {:response-keys [:activity/uuid
                                {:activity/assessment [:assessment/can-test-out?
                                                       :assessment/pre-test-pass-percent
                                                       :assessment/pass-percent
                                                       :assessment/confidence-threshold-enabled?
                                                       :assessment/confidence-threshold
                                                       :assessment/max-attempts
                                                       :assessment/results-display-strategy
                                                       :assessment/feedback-display-strategy
                                                       :assessment/show-answers-and-feedback-when-passed-or-exhausted?
                                                       :assessment/settings-confirmed?]}]
                :on-success-toast  "Assessment updated."
                :on-failure-toast "Failed to update your assessment."
                :on-success (fn [] (re-frame/dispatch [:router/nav-to post-update-nav-to-url]))}])
             (re-frame/dispatch
              [questionnaire-update-event
               (cond-> (cond-> (assoc % :activity/uuid activity-uuid)
                         (false? ((nq :confidence-threshold-enabled?) %))
                         (dissoc (nq :confidence-threshold)))
                 (some? ((nq :uuid) questionnaire))
                 (assoc :questionnaire/uuid ((nq :uuid) questionnaire)))
               {:response-keys [:questionnaire/uuid
                                :questionniare/can-test-out?
                                :questionnaire/pre-test-pass-percent
                                :questionnaire/pass-percent
                                :questionnaire/confidence-threshold-enabled?
                                :questionnaire/confidence-threshold
                                :questionnaire/max-attempts
                                :questionnaire/results-display-strategy
                                :questionnaire/feedback-display-strategy]
                :on-success-toast "Boost updated!"
                :on-failure-toast "Failed to update boost."}]))}}

        [:div {:class (:settings-form-container classes)}
         (when-not (= questionnaire-type :floating)
           [:<>

            ;; Test-out Enabled
            [:div {:class (:settings-field-container classes)}
             [:div {:style {:margin-right (gs [:spacing :p20])}}
              [text/title-20 :p "Test-out enabled"]
              [text/caption-10 :p
               (str "Allow a learner to skip an activity, provided they can "
                    "demonstrate knowledge/competence in their Pre-Test "
                    "performance.")]]
             (rf/custom-field
              {:field-key (nq :can-test-out?)
               :id :test-out
               :render toggle/render
               :validators []
               :transformers []
               :default-value (boolean can-test-out?)})]

            ;; Pre-test pass threshold. Hidden when Test-out is false
            (rf/custom-field
             {:field-key (nq :pre-test-pass-percent)
              :id :pre-test-pass-percent
              :hide-on #(not (get-in % [(nq :can-test-out?) :data]))
              :render pass-threshold/render
              :render-percent-display-field render-percent-display-field
              :total-number pre-test-question-count
              :options (mapv (fn [question-idx]
                               {:value question-idx
                                :display question-idx})
                             (range 1 (inc pre-test-question-count)))
              :default-value (if (some? pre-test-pass-percent)
                               (ku/percent->passing-limit
                                pre-test-pass-percent
                                pre-test-question-count)
                               :no-score)
              :validators []
              :masks []
              :transformers [#(if (= :no-score %)
                                nil
                                (/ (ku/calc-percent-floored pre-test-question-count %)
                                   100))]})

            ;; Confidence threshold toggle
            (rf/custom-field
             {:field-key (nq :confidence-threshold-enabled?)
              :id :confidence-threshold-enabled
              :hide-on #(not (get-in % [(nq :can-test-out?) :data]))
              :render confidence-threshold-toggle/render
              :default-value true})

            ;; Confidence threshold
            (rf/custom-field
             {:field-key (nq :confidence-threshold)
              :test-out-field-key (nq :pre-test-pass-percent)
              :confidence-threshold-field-key (nq :confidence-threshold-enabled?)
              :id :confidence-threshold
              :hide-on #(not (get-in % [(nq :can-test-out?) :data]))
              :render confidence-threshold/render
              :total-number pre-test-question-count
              :default-value (or confidence-threshold "advanced")
              :options confidence-threshold/threshold-options})

            [divider]])

         [:div {:class (:settings-field-container classes)}
          [:div {:style {:margin-right (gs [:spacing :p20])
                         :flex-grow 1}}
           [text/title-20 :p (if (= questionnaire-type :floating)
                               "Questionnaire Pass Performance"
                               "Post-Test Pass Performance")]
           [text/caption-10 :p
            (str "How many questions must the learner get correct in order "
                 "to pass the "
                 (if (= questionnaire-type :floating)
                   "questionnaire?"
                   "post-test?"))]]

          (if (zero? (if (= questionnaire-type :floating)
                       question-count
                       post-test-question-count))
            [text/body-20 :p
             "To set a pass percentage, add graded questions to your assessment"]

            [:<>
             (rf/custom-field
              {:field-key (nq :pass-percent)
               :id :pass-percent
               :render select/render
               :options (cond->> (mapv (fn [question-idx]
                                         {:value question-idx
                                          :display question-idx})
                                       (range 1 (inc (if (= questionnaire-type :floating)
                                                       question-count
                                                       post-test-question-count))))
                          (= questionnaire-type :floating)
                          (into [{:value :no-score
                                  :display "Ungraded"}]))
               :default-value (if (some? pass-percent)
                                (ku/percent->passing-limit pass-percent
                                                           (if (= questionnaire-type :floating)
                                                             question-count
                                                             post-test-question-count))
                                :no-score)
               :validators []
               :masks []
               :transformers [#(if (= :no-score %)
                                 nil
                                 (/ (ku/calc-percent-floored (if (= questionnaire-type :floating)
                                                               question-count
                                                               post-test-question-count) %)
                                    100))]})

             (rf/custom-field
              {:field-key (nq :pass-percent)
               :id :percent-display
               :total-number (if (= questionnaire-type :floating)
                               question-count
                               post-test-question-count)
               :render render-percent-display-field})])]

         [divider]

         [:div {:class (:settings-field-container classes)}
          [:div
           [text/title-20 :p (if (= questionnaire-type :floating)
                               "Questionnaire Retry Attempts"
                               "Maximum Post-Test Attempts")]
           [text/caption-10 :p
            (str "How many times can the learner attempt the "
                 (if (= questionnaire-type :floating)
                   "questionnaire?"
                   "post-test?"))]]

          (rf/custom-field
           {:field-key (nq :max-attempts)
            :id :max-attempts
            :render select/render
            :options (into [{:value :no-maximum :display "No maximum"}]
                           (mapv (fn [question-idx]
                                   {:value (str question-idx)
                                    :display question-idx})
                                 (range 1
                                        (inc ac/max-post-test-retries))))
            :default-value (if (some? max-attempts)
                             (str max-attempts)
                             :no-maximum)
            :validators []
            :masks []
            :transformers [#(if (= % :no-maximum) nil (js/parseInt %))]})]

         [divider]

         [:div {:class (:settings-field-container classes)}
          [:div
           [text/title-20 :p "Test Results Display Strategy"]
           [text/caption-10 :p
            (str "What information would you like to present to the learner "
                 "if they fail the "
                 (if (= questionnaire-type :floating)
                   "questionnaire?"
                   "post-test?"))]

           (rf/custom-field
            {:field-key (nq :results-display-strategy)
             :id :results-display-strategy
             :render box-radio/render
             :options
             [{:value "no-results"
               :title "Display Grade Only"
               :description
               (str "The learner will only see their grade. They "
                    "will NOT see which questions they got right "
                    "and wrong OR which answers are correct.")}

            ;; Send ONLY the correctness of their answer
              {:value "send-individual-question-correctness"
               :title "Display Grade + Question Level Correctness"
               :description
               (str "The learner will see their grade PLUS which "
                    "questions they got right and wrong. They will "
                    "NOT see which answers are correct.")}

            ;; Send the correct answer AND correctness
              {:value "send-individual-question-answer"
               :title "Display Grade + Question and Answer Level Correctness"
               :description
               (str "The learner will see their grade PLUS which "
                    "questions they got right and wrong. AS WELL AS "
                    "which answers are correct.")}]
             :default-value results-display-strategy
             :validators []
             :masks []
             :transformers []})]]

         [:div {:style {:padding-left (gs [:spacing :p20])}}
          (rf/input
           {:field-key (nq :show-answers-and-feedback-when-passed-or-exhausted?)
            :type :checkbox
            :data-test :show-answers-post-test-complete
            :name :show-answers-and-feedback-when-passed-or-exhausted?
            :id :show-answers-and-feedback-when-passed-or-exhausted?
            :default-value show-answers-and-feedback-when-passed-or-exhausted?
            :label "Checking here will show correct answers, Feedback Copy, 
                           and resources to the Learner upon passing the Post-Test 
                           or exhausting Post-Test attempts"})]

         (when (true? is-super-user)
           [:div {:style {:padding (gs [:spacing :p20])}}
            (rf/custom-field
             {:field-key (nq :feedback-display-strategy)
              :id :feedback-display-strategy
              :title "Feedback Display Strategy"
              :render radio/render
              :classes classes
              :options [{:value "always-show"
                         :label (str "Always show question-level feedback "
                                     "regardless of if the learner passes "
                                     "or fails the test")}
                        {:value "pass-only"
                         :label "Only show question-level feedback when the learner passes the 
                                      test, unless the checkbox is checked in the Test Results Display Strategy section"}]
              :default-value (if (some? feedback-display-strategy)
                               feedback-display-strategy
                               "pass-only")})])

         [:div {:class (:cta-container classes)}
          (rf/submit-button
           {:default-text (if (false? settings-confirmed?) "Confirm Settings" "Update Settings")})

        ;; TODO Wrap in permissions
          [button/admin-button
           {:on-click
            #(do
               (.preventDefault %)
               (re-frame/dispatch
                [:notifier/create-alert
                 {:title (str "Delete " (case questionnaire-type
                                          :assessments "assessment"
                                          :evaluation "evaluation"
                                          :floating "question(s)"

                                          (js/console.error
                                           "Invalid questionnaire type"))
                              "?")
                  :message (str "When deleting "
                                (case questionnaire-type
                                  :assessments "an assessment"
                                  :evaluation "an evaluation"
                                  :floating "a questionnaire"

                                  (js/console.error "Invalid questionnaire type"))
                                ", all questions assigned will "
                                "also be removed.")
                  :confirm-action
                  (fn []
                    (re-frame/dispatch
                     (if (= :assessments questionnaire-type)
                       [questionnaire-delete-event
                        {:activity/uuid activity-uuid}
                        {:on-success-toast "Assessment removed."
                         :on-failure-toast "Failed to delete, please try again."
                         :on-success (fn []
                                       (re-frame/dispatch [:router/nav-to post-delete-nav-to-url]))}]
                       [questionnaire-delete-event
                        {:activity/uuid activity-uuid
                         :questionnaire/uuid questionnaire-uuid
                         :boost/uuid boost-uuid}
                        {:on-success-toast "Question(s) removed."
                         :on-failure-toast (str "Something when wrong. Try to remove your "
                                                "questionnaire again.")
                         :on-success (fn []
                                       (re-frame/dispatch [:boosts/remove-boost-questionnaire-success {:boost-uuid boost-uuid}])
                                       (re-frame/dispatch [:router/nav-to
                                                           (str (au/->admin-url {:activity-uuid activity-uuid
                                                                                 :curriculum-uuid curriculum-uuid})
                                                                "/boosts/" boost-uuid "/edit")]))}])))
                  :confirm-text (str "Delete " model-name)}]))}
           (str "Delete " model-name)]]]]])))
